import Vue from 'vue'
import {APP_VERSION, FOOD_UPDATE_REQUIRED} from "@utils/storageKeys";
import {applyFeatureParam} from "@utils/featureFlags";
import {
  AlertPlugin, BadgePlugin, BreadcrumbPlugin, ButtonPlugin, CardPlugin, CollapsePlugin, DropdownPlugin,
  FormPlugin, FormCheckboxPlugin, FormGroupPlugin, FormInputPlugin, FormRadioPlugin, FormSelectPlugin,
  FormTextareaPlugin, InputGroupPlugin, LayoutPlugin, ModalPlugin, NavPlugin, OverlayPlugin, PaginationPlugin,
  PopoverPlugin, ProgressPlugin, SidebarPlugin, SkeletonPlugin, SpinnerPlugin, TablePlugin, TabsPlugin, TooltipPlugin,
} from 'bootstrap-vue'
import Notifications from 'vue-notification'
import {confirm} from '@components/global/confirm';
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css';
import localforage from "localforage";
// searchBar's unscoped toolbar styles must land in app.css ahead of app.vue's @design,
// whose rules (e.g. .form-control:focus) override them.
import '@components/searchBar.vue';
import App from './app'
import router, {axiosFailInterceptor} from '@router'
import store from '@state/store'
// Eager so it stays ahead of design/custom/plugins/_dropzone.scss in the cascade.
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import AthlisisMixin from "@src/mixins/BaseMixin";
import EducationalComponent from "@components/EducationalComponent";
import EducationalGatedBModal from "@components/EducationalGatedBModal";
import i18n from "./utils/setupi18n";
import axios from "axios";
import SaveButton from "@components/elements/SaveButton";
import DeleteButton from "@components/elements/DeleteButton";
import AppSidePanel from "@components/elements/AppSidePanel";
import PanelCard from "@components/elements/PanelCard";
import PasscodeGate from "@components/global/PasscodeGate";
import AppNotice from "@components/global/AppNotice";
import EmptyState from "@components/global/Empty";
import decimalInputDirective from "@src/directives/decimalInputDirective";
import safeHtmlDirective from "@src/directives/safeHtmlDirective";
import { registerSafeQuillClipboard } from "@utils/safeQuillClipboard";
import {applyCategoryScheme} from "@utils/mealCategoryColors";

import '@src/design/tailwind.css';
import {getCoreAxiosInstance} from "@utils/axios";
import { VueQueryPlugin } from '@tanstack/vue-query';
import { queryClient } from '@src/queries/client';
// Only the bootstrap-vue plugins the templates use; add one here before using a new b-* component.
[
  AlertPlugin, BadgePlugin, BreadcrumbPlugin, ButtonPlugin, CardPlugin, CollapsePlugin, DropdownPlugin,
  FormPlugin, FormCheckboxPlugin, FormGroupPlugin, FormInputPlugin, FormRadioPlugin, FormSelectPlugin,
  FormTextareaPlugin, InputGroupPlugin, LayoutPlugin, ModalPlugin, NavPlugin, OverlayPlugin, PaginationPlugin,
  PopoverPlugin, ProgressPlugin, SidebarPlugin, SkeletonPlugin, SpinnerPlugin, TablePlugin, TabsPlugin, TooltipPlugin,
].forEach((plugin) => Vue.use(plugin));

const currentVersion = "3.3";

if(!localStorage.getItem(APP_VERSION) || localStorage.getItem(APP_VERSION) !== currentVersion){
  localStorage.setItem(APP_VERSION, currentVersion);
  localStorage.setItem(FOOD_UPDATE_REQUIRED, 'true');
}

localforage.config({name: 'athlisis'});
applyFeatureParam(window.location.search);

applyCategoryScheme(store.state.dietPlan.categoryScheme);

// Don't warn about using the dev version of Vue in development.
Vue.config.productionTip = process.env.NODE_ENV === 'production';

// External web component mounted by the Prolipsis script (app.vue) — tell Vue
// it's not an unregistered component.
Vue.config.ignoredElements = ['prolipsis-widget'];

Vue.component('save-button', SaveButton);
Vue.component('delete-button', DeleteButton);
Vue.component('app-side-panel', AppSidePanel);
Vue.component('panel-card', PanelCard);
Vue.component('passcode-gate', PasscodeGate);
Vue.component('app-notice', AppNotice);
Vue.component('empty-state', EmptyState);
Vue.directive('decimal', decimalInputDirective);
Vue.directive('safe-html', safeHtmlDirective);
registerSafeQuillClipboard();
Vue.use(Notifications);
Vue.prototype.$dialog = {confirm};
Vue.use(VueQueryPlugin, { queryClient });


// vue-select's empty-search text is English; every picker shows ours unless it passes its own #no-options.
Vue.component('v-select', {
  functional: true,
  render: (h, { data, children, scopedSlots }) => h(vSelect, {
    ...data,
    scopedSlots: { 'no-options': () => i18n.t('global.noResults'), ...scopedSlots },
  }, children),
});

Vue.prototype.$axios = getCoreAxiosInstance();

Vue.mixin(AthlisisMixin);

Vue.component('educational-unavailable', EducationalComponent);
Vue.component('educational-gated-b-modal', EducationalGatedBModal);

// Set this here, to make sure we can always resolve this.
Vue.prototype._i18n = i18n

const app = new Vue({
  i18n,
  router,
  store,
  render: (h) => h(App),
}).$mount('#app');

import { initAnalytics } from '@utils/analytics';
initAnalytics();

import { watchForNewBuild } from '@utils/appVersion';
watchForNewBuild();
